import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import { setLogoutCallback, storeApi } from '../services/api';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from storage, then refresh from API
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('rodbot_token');
        const savedStoreId = localStorage.getItem('rodbot_store_id');
        const savedUser = localStorage.getItem('rodbot_user');

        if (savedToken && savedStoreId) {
          setToken(savedToken);
          setStoreId(savedStoreId);
          setIsAuthenticated(true);
          
          let parsedUser = null;
          if (savedUser) {
            parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
          }

          // Refresh user data from API (fills in email/profile_picture_url for old sessions)
          try {
            const storeInfo = await storeApi.getInfo();
            if (storeInfo.success && storeInfo.data) {
              const freshUser = {
                ...(parsedUser || {}),
                email: storeInfo.data.email || parsedUser?.email || null,
                profile_picture_url: storeInfo.data.profile_picture_url || parsedUser?.profile_picture_url || null,
                features: storeInfo.data.features || parsedUser?.features || null,
              };
              setUser(freshUser);
              localStorage.setItem('rodbot_user', JSON.stringify(freshUser));
            }
          } catch (refreshErr) {
            // Non-critical: user data just won't be refreshed
            console.warn('Could not refresh user data:', refreshErr);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid data
        localStorage.removeItem('rodbot_token');
        localStorage.removeItem('rodbot_store_id');
        localStorage.removeItem('rodbot_user');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      if (response.success) {
        const { token: newToken, store, email: userEmail, features, profile_picture_url } = response;
        
        // Store in state
        setToken(newToken);
        setStoreId(store);
        setUser({ email: userEmail, features: features || null, profile_picture_url: profile_picture_url || null });
        setIsAuthenticated(true);
        
        // Store in localStorage
        localStorage.setItem('rodbot_token', newToken);
        localStorage.setItem('rodbot_store_id', store);
        localStorage.setItem('rodbot_user', JSON.stringify({ email: userEmail, features: features || null, profile_picture_url: profile_picture_url || null }));
        
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  }, []);

  const register = useCallback(async ({ name, email, phone, password }) => {
    try {
      const response = await authService.register({ name, email, phone, password });

      if (response.success && response.token) {
        const { token: newToken, store, email: userEmail, features, profile_picture_url } = response;

        setToken(newToken);
        setStoreId(store);
        setUser({ email: userEmail, features: features || null, profile_picture_url: profile_picture_url || null });
        setIsAuthenticated(true);

        localStorage.setItem('rodbot_token', newToken);
        localStorage.setItem('rodbot_store_id', store);
        localStorage.setItem('rodbot_user', JSON.stringify({ email: userEmail, features: features || null, profile_picture_url: profile_picture_url || null }));

        return { success: true };
      }

      return { success: false, error: response.error || 'Registration failed' };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('rodbot_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const logout = useCallback(() => {
    // Clear state
    setUser(null);
    setToken(null);
    setStoreId(null);
    setIsAuthenticated(false);
    
    // Clear storage
    localStorage.removeItem('rodbot_token');
    localStorage.removeItem('rodbot_store_id');
    localStorage.removeItem('rodbot_user');
  }, []);

  // Register logout callback with API service
  useEffect(() => {
    setLogoutCallback(logout);
  }, [logout]);

  const value = {
    user,
    token,
    storeId,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

